// Attaches an eSCR (eCourts High Court) judgment to an ALREADY-exported case as
// an order, instead of creating a new case. The judgment is matched to the
// user's saved cases by CNR or case number; on a match it is appended to that
// case's `orders` array, which renders in the CaseDetails "Orders" tab.
//
// eSCR PDFs are served by our backend at a stable URL (/api/v1/highcourt/pdf/
// :cnr — re-fetchable any time), so we store that absolute URL on the order as
// `pdfStorageUrl` (which CaseDetails' High Court order "View" opens directly).
// No Firebase Storage persistence is needed.

import { db } from "../firebase";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { pdfUrlFor } from "./escrApi";
import { saveOrderPdfsToFiles } from "../pages/HighCourtFetchCases/highCourtOrderFiles";

// Cases live across these collections (status buckets), same as High Court export.
const COLLECTIONS = ["pending", "disposed", "decided"];

// Loose identifier compare — case numbers/CNRs differ only in punctuation/case.
const norm = (s) => String(s || "").replace(/[^a-z0-9]/gi, "").toUpperCase();

function matchesJudgment(caseData, judgment) {
  const jCnr = norm(judgment.cnr);
  const jCaseNo = norm(judgment.caseNumber);
  if (jCnr && norm(caseData.cino) === jCnr) return true;
  if (jCaseNo) {
    if (norm(caseData.caseNo) === jCaseNo) return true;
    if (norm(caseData.caseNumber) === jCaseNo) return true;
    if (norm(caseData.filingNumber) === jCaseNo) return true;
  }
  return false;
}

/**
 * Find the user's exported case that this judgment belongs to (by CNR or case
 * number). Returns { caseId, collection, caseNo, orders } or null.
 */
export async function findExportedCaseForJudgment(judgment, uid) {
  for (const col of COLLECTIONS) {
    const snap = await getDocs(query(collection(db, col), where("owner", "==", uid)));
    for (const d of snap.docs) {
      const data = d.data();
      if (matchesJudgment(data, judgment)) {
        return {
          caseId: d.id,
          collection: col,
          caseNo: data.caseNo || data.caseNumber || judgment.caseNumber || "",
          orders: Array.isArray(data.orders) ? data.orders : [],
        };
      }
    }
  }
  return null;
}

function buildOrderEntry(judgment, existingCount) {
  return {
    orderNumber: String(existingCount + 1),
    orderOn: judgment.caseNumber || "",
    judge: judgment.judgeName || "",
    orderDate: judgment.decisionDate || judgment.registrationDate || "",
    // Stable absolute eSCR backend URL — CaseDetails opens this directly.
    pdfStorageUrl: pdfUrlFor(judgment) || "",
    cnr: judgment.cnr || "",
    caseTitle: judgment.caseTitle || "",
    disposalNature: judgment.disposalNature && judgment.disposalNature !== "N/A" ? judgment.disposalNature : "",
    source: "eSCR",
    addedAt: new Date().toISOString(),
  };
}

// True when an existing order already represents this judgment, so re-exporting
// (or exporting a judgment the case already has as an order) is a no-op.
function isAlreadyPresent(existing, judgment, pdfUrl) {
  const jCnr = norm(judgment.cnr);
  const jCaseNo = norm(judgment.caseNumber);
  const jDate = norm(judgment.decisionDate || judgment.registrationDate);
  return existing.some((o) => {
    if (pdfUrl && o.pdfStorageUrl === pdfUrl) return true;
    if (jCnr && (norm(o.cnr) === jCnr || norm(o.cino) === jCnr)) return true;
    // Same case number AND same date → same order document.
    if (jCaseNo && jDate && norm(o.orderOn) === jCaseNo && norm(o.orderDate) === jDate) return true;
    return false;
  });
}

/**
 * Append the judgment to the matched case's orders AND mirror it into the case's
 * Files "Orders" folder. Deduped — never adds a duplicate order.
 * @returns {Promise<{added: boolean, reason?: string, order?: object}>}
 */
export async function attachJudgmentToCase({ judgment, match, ownerId }) {
  const pdfUrl = pdfUrlFor(judgment) || "";
  const existing = match.orders || [];
  if (isAlreadyPresent(existing, judgment, pdfUrl)) {
    return { added: false, reason: "duplicate" };
  }

  const order = buildOrderEntry(judgment, existing.length);
  await updateDoc(doc(db, match.collection, match.caseId), { orders: [...existing, order] });

  // Mirror the judgment PDF into the case's Files tab under the "Orders" folder
  // (best-effort — never blocks the Orders-tab add).
  try {
    await saveOrderPdfsToFiles([order], match.caseId, match.caseNo, match.collection, ownerId);
  } catch (e) {
    console.warn("Could not mirror judgment into Files Orders folder:", e?.message);
  }

  return { added: true, order };
}
